import React from 'react';
import { useAuth } from '../features/auth/useAuth';

interface UserInfoProps {
  showSessionInfo?: boolean;
  className?: string;
}

export const UserInfo: React.FC<UserInfoProps> = ({ 
  showSessionInfo = false, 
  className = '' 
}) => {
  const { user, timeUntilExpiration, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return null;
  }

  const formatTime = (milliseconds: number): string => {
    const hours = Math.floor(milliseconds / 3600000);
    const minutes = Math.floor((milliseconds % 3600000) / 60000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getSessionStatus = (): { color: string; text: string } => {
    const thirtyMinutes = 30 * 60 * 1000;
    const fiveMinutes = 5 * 60 * 1000;
    
    if (timeUntilExpiration <= fiveMinutes) {
      return { color: 'text-red-600', text: 'Critical' };
    } else if (timeUntilExpiration <= thirtyMinutes) {
      return { color: 'text-yellow-600', text: 'Warning' };
    }
    return { color: 'text-green-600', text: 'Active' };
  };

  const sessionStatus = getSessionStatus();

  return (
    <div className={`flex items-center space-x-4 ${className}`}>
      {/* User Avatar and Info */}
      <div className="flex items-center space-x-3">
        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
          <span className="text-sm font-medium text-blue-600">
            {user.firstName?.charAt(0) || user.name?.charAt(0) || 'U'}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">
            {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim()}
          </span>
          <span className="text-xs text-gray-500 capitalize">
            {user.role?.toLowerCase().replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Session Information */}
      {showSessionInfo && (
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1">
            <div className={`w-2 h-2 rounded-full ${
              sessionStatus.color === 'text-red-600' ? 'bg-red-500' :
              sessionStatus.color === 'text-yellow-600' ? 'bg-yellow-500' : 'bg-green-500'
            }`}></div>
            <span className={`text-xs font-medium ${sessionStatus.color}`}>
              {sessionStatus.text}
            </span>
          </div>
          <span className="text-xs text-gray-500">
            {formatTime(timeUntilExpiration)} left
          </span>
        </div>
      )}
    </div>
  );
};

export default UserInfo;
